/**
 * Aggregators Tab Component
 * Manages UberEats and DoorDash integration setup
 */

import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Truck, 
  Car, 
  CheckCircle, 
  AlertCircle,
  Save,
  Eye,
  EyeOff,
  ExternalLink
} from 'lucide-react';
import { SuperAdminOnboardingData, getStatusBadgeVariant, getStatusDisplayName } from '@/types/super-admin-onboarding';
import { OnboardingStatus, SetupMethod } from '@/types/onboarding';
import { toast } from '@/hooks/use-toast';

interface AggregatorsTabProps {
  data: SuperAdminOnboardingData;
  onFieldUpdate: (field: keyof SuperAdminOnboardingData, value: any) => Promise<void>;
  onBulkFieldUpdate?: (updates: Record<string, any>) => Promise<{ success: boolean; changedCount: number }>;
  onStatusUpdate: (status: OnboardingStatus) => Promise<void>;
}

const AggregatorsTab: React.FC<AggregatorsTabProps> = ({
  data,
  onFieldUpdate,
  onBulkFieldUpdate,
  onStatusUpdate
}) => {
  const [formData, setFormData] = useState({
    // UberEats
    uber_aggregation_status: data.uber_aggregation_status || OnboardingStatus.NOT_STARTED,
    uber_setup_method: data.uber_setup_method || SetupMethod.REMOTE_ACCESS,
    ubereats_store_id: data.ubereats_store_id || '',
    
    // DoorDash
    doordash_aggregation_status: data.doordash_aggregation_status || OnboardingStatus.NOT_STARTED,
    doordash_setup_method: data.doordash_setup_method || SetupMethod.REMOTE_ACCESS,
    doordash_store_id: data.doordash_store_id || '',
    doordash_login: data.doordash_login || '',
    doordash_pin: data.doordash_pin || ''
  });
  
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDoorDashPin, setShowDoorDashPin] = useState(true); // Default to showing PIN

  // Handle form field changes
  const handleFieldChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // Save all changes
  const handleSave = async () => {
    try {
      setSaving(true);
      
      if (onBulkFieldUpdate) {
        // Use the new bulk update function
        const result = await onBulkFieldUpdate(formData);
        
        if (result.changedCount === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
        } else {
          toast({
            title: "Success",
            description: `Updated ${result.changedCount} field(s) successfully`
          });
        }
      } else {
        // Fallback to individual updates if bulk update not available
        const changedFields = Object.entries(formData).filter(([field, value]) => {
          const originalValue = data[field as keyof SuperAdminOnboardingData];
          return originalValue !== value;
        });
        
        if (changedFields.length === 0) {
          toast({
            title: "No Changes",
            description: "No fields were modified"
          });
          return;
        }
        
        const updates = changedFields.map(([field, value]) => 
          onFieldUpdate(field as keyof SuperAdminOnboardingData, value)
        );
        
        await Promise.all(updates);
        
        toast({
          title: "Success",
          description: `Updated ${changedFields.length} field(s) successfully`
        });
      }
      
      setHasChanges(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save order aggregators information",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Update specific aggregator status
  const handleAggregatorStatusUpdate = async (platform: 'uber' | 'doordash', status: OnboardingStatus) => {
    try {
      // Save changes first if any
      if (hasChanges) {
        await handleSave();
      }
      
      const field = platform === 'uber' ? 'uber_aggregation_status' : 'doordash_aggregation_status';
      await onFieldUpdate(field, status);
      
      // Update local state
      setFormData(prev => ({
        ...prev,
        [field]: status
      }));
      
      toast({
        title: "Success",
        description: `${platform === 'uber' ? 'UberEats' : 'DoorDash'} status updated to ${getStatusDisplayName(status)}`
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update status",
        variant: "destructive"
      });
    }
  };

  const uberComplete = formData.uber_aggregation_status === OnboardingStatus.COMPLETE;
  const doordashComplete = formData.doordash_aggregation_status === OnboardingStatus.COMPLETE;
  const hasUberInfo = formData.ubereats_store_id;
  const hasDoorDashInfo = formData.doordash_store_id && formData.doordash_login && formData.doordash_pin;

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <Truck className="h-5 w-5 text-primary" />
              </div>
              <div>
                <CardTitle>Order Aggregators</CardTitle>
                <p className="text-sm text-muted-foreground">
                  Integrate with UberEats and DoorDash delivery platforms
                </p>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>


      {/* UberEats Setup */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Car className="h-5 w-5" />
              UberEats Integration
            </CardTitle>
            <Badge variant={getStatusBadgeVariant(formData.uber_aggregation_status)}>
              {getStatusDisplayName(formData.uber_aggregation_status)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ubereats_store_id">UberEats Store ID (if known)</Label>
            <Input
              id="ubereats_store_id"
              value={formData.ubereats_store_id}
              onChange={(e) => handleFieldChange('ubereats_store_id', e.target.value)}
              placeholder="Enter UberEats store ID"
            />
            <p className="text-sm text-muted-foreground">
              Optional: Provide if the restaurant already has a UberEats account.
            </p>
          </div>

          {/* UberEats Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('uber', OnboardingStatus.NOT_STARTED)}
            >
              Mark UberEats Not Started
            </Button>
            
            <Button 
              variant="default"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('uber', OnboardingStatus.AWAITING_CONFIRMATION)}
            >
              Mark UberEats In Progress
            </Button>
            
            <Button 
              variant="default"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('uber', OnboardingStatus.COMPLETE)}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark UberEats Complete
            </Button>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('uber', OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Needs Attention
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* DoorDash Setup */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Truck className="h-5 w-5" />
              DoorDash Integration
            </CardTitle>
            <Badge variant={getStatusBadgeVariant(formData.doordash_aggregation_status)}>
              {getStatusDisplayName(formData.doordash_aggregation_status)}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="doordash_store_id">DoorDash Store ID</Label>
            <Input
              id="doordash_store_id"
              value={formData.doordash_store_id}
              onChange={(e) => handleFieldChange('doordash_store_id', e.target.value)}
              placeholder="Enter DoorDash store ID"
            />
            <p className="text-sm text-muted-foreground">
              Required for API integration and order synchronization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="doordash_login">DoorDash Login/Email</Label>
              <Input
                id="doordash_login"
                value={formData.doordash_login}
                onChange={(e) => handleFieldChange('doordash_login', e.target.value)}
                placeholder="merchant@example.com"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="doordash_pin">DoorDash Account PIN</Label>
              <div className="relative">
                <Input
                  id="doordash_pin"
                  type={showDoorDashPin ? "text" : "password"}
                  value={formData.doordash_pin}
                  onChange={(e) => handleFieldChange('doordash_pin', e.target.value)}
                  placeholder="4-digit PIN"
                  maxLength={4}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 hover:bg-transparent"
                  onClick={() => setShowDoorDashPin(!showDoorDashPin)}
                >
                  {showDoorDashPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-amber-50 dark:bg-amber-950/50 rounded-lg border border-amber-200 dark:border-amber-800">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              <strong>Note:</strong> DoorDash Store ID and credentials are needed for API integration and order synchronization.
            </p>
          </div>

          {/* DoorDash Actions */}
          <div className="flex flex-wrap gap-2 pt-4 border-t">
            <Button 
              variant="outline"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('doordash', OnboardingStatus.NOT_STARTED)}
            >
              Mark DoorDash Not Started
            </Button>
            
            <Button 
              variant="default"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('doordash', OnboardingStatus.AWAITING_CONFIRMATION)}
            >
              Mark DoorDash In Progress
            </Button>
            
            <Button 
              variant="default"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('doordash', OnboardingStatus.COMPLETE)}
            >
              <CheckCircle className="h-4 w-4 mr-2" />
              Mark DoorDash Complete
            </Button>
            
            <Button 
              variant="outline"
              size="sm"
              onClick={() => handleAggregatorStatusUpdate('doordash', OnboardingStatus.NEEDS_ATTENTION)}
            >
              <AlertCircle className="h-4 w-4 mr-2" />
              Needs Attention
            </Button>
          </div>
        </CardContent>
      </Card>


      {/* Main Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button 
              onClick={handleSave} 
              disabled={!hasChanges || saving}
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
          
          <div className="p-4 bg-muted/50 rounded-lg">
            <h4 className="font-medium mb-2">Integration Status</h4>
            <div className="space-y-1 text-sm text-muted-foreground">
              <div>UberEats: {getStatusDisplayName(formData.uber_aggregation_status)}</div>
              <div>DoorDash: {getStatusDisplayName(formData.doordash_aggregation_status)}</div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              Both platforms are optional but recommended for maximum order volume and customer reach.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AggregatorsTab;